import { useStores } from '@littlebird/sdk';
import { useNavigate } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { type ReactNode, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Command } from '@/components/ui/cmdk/cmdk';
import { EntityIcon } from '@/components/ui/icon-picker';
import { Popover } from '@/components/ui/popover';

import { PROJECT_LABELS, useMoveToProject } from './move-to-project';
import { type ProjectAssignmentTarget } from './project-actions';
import { useLoadAllProjectPages } from './use-load-all-project-pages';

const MIN_PROJECTS_FOR_SEARCH = 6;

export const ProjectPickerPopover = observer(function ProjectPickerPopover({
  target,
  children,
  align = 'start',
}: {
  target: ProjectAssignmentTarget;
  children: ReactNode;
  align?: 'start' | 'end';
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const commandRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { projects } = useStores();
  const {
    currentProject,
    projectOptions,
    handleSelectProject,
    clearProject,
    showCreateProjectModal,
  } = useMoveToProject(target);
  const loadFailed = useLoadAllProjectPages(open);

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredProjects = projectOptions.filter((project) =>
    project.name.toLowerCase().includes(normalizedQuery)
  );
  const showSearch = projectOptions.length >= MIN_PROJECTS_FOR_SEARCH;

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) setSearchQuery('');
  };

  const closeAnd = (action: () => void) => {
    handleOpenChange(false);
    action();
  };

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Content
        className="w-64 p-0"
        align={align}
        onOpenAutoFocus={(event) => {
          if (showSearch) return;
          event.preventDefault();
          commandRef.current?.focus();
        }}
      >
        <Command.Root ref={commandRef} shouldFilter={false}>
          {showSearch && (
            <Command.Input
              autoFocus
              placeholder="Search projects..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
          )}
          <Command.List>
            {normalizedQuery && filteredProjects.length === 0 && (
              <div className="py-6 text-center text-sm text-foreground-subtle">
                No projects found
              </div>
            )}
            {filteredProjects.length > 0 && (
              <Command.Group>
                {filteredProjects.map((project) => (
                  <Command.Item
                    key={project.id}
                    value={project.id}
                    onSelect={() =>
                      closeAnd(() => handleSelectProject(project.id))
                    }
                  >
                    <EntityIcon icon={project.icon} muted />
                    <span className="flex-1 truncate">{project.name}</span>
                    {project.isSelected ? (
                      <Icons.check className="ml-auto size-3.5 shrink-0 opacity-75" />
                    ) : (
                      <span className="ml-auto tabular-nums text-foreground-muted">
                        {projects.getItemCounts(project.id).threads +
                          projects.getItemCounts(project.id).meetings}
                      </span>
                    )}
                  </Command.Item>
                ))}
              </Command.Group>
            )}
            {filteredProjects.length > 0 && (
              <Command.Separator
                alwaysRender
                className="my-1 bg-border dark:bg-border-strong"
              />
            )}
            <Command.Group>
              {!normalizedQuery && currentProject && (
                <>
                  <Command.Item
                    value="open-project"
                    onSelect={() =>
                      closeAnd(() =>
                        navigate({ to: `/projects/${currentProject.id}` })
                      )
                    }
                  >
                    <Icons.arrowUpRight className="size-3.5 shrink-0 opacity-75" />
                    Open project
                  </Command.Item>
                  <Command.Item
                    value="remove-from-project"
                    onSelect={() => closeAnd(clearProject)}
                  >
                    <Icons.circleDashed className="size-3.5 shrink-0 opacity-75" />
                    {PROJECT_LABELS.none}
                  </Command.Item>
                </>
              )}
              <Command.Item
                value="create-project"
                onSelect={() => closeAnd(showCreateProjectModal)}
              >
                <Icons.folderPlus className="size-3.5 shrink-0 opacity-75" />
                {PROJECT_LABELS.create}
              </Command.Item>
            </Command.Group>
            {loadFailed && (
              <div className="px-3 py-1.5 text-xs text-foreground-subtle">
                Some projects failed to load
              </div>
            )}
          </Command.List>
        </Command.Root>
      </Popover.Content>
    </Popover.Root>
  );
});
